import { api } from "../../lib";

export interface UserGroup {
  id: string;
  groupId: string;
  group?: { id: string; name: string } | null;
}

export interface User {
  id: string;
  username: string;
  email?: string;
  full_name: string;
  credentials?: string | null;
  role_id: string | null;
  facility_id: string | null;
  pathologist_maximum_daily_cases: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  role?: { name: string } | null;
  facility?: { name: string } | null;
  userGroups?: UserGroup[];
  is_default_qa_reviewer?: boolean;
  invitationStatus?: string;
  invitedAt?: string | null;
  activatedAt?: string | null;
  invitation_status?: string;
  invited_at?: string | null;
  activated_at?: string | null;
  passkeyCount?: number;
  warning?: string;
  signatureImageUrl?: string | null;
  signatureImagePreviewUrl?: string | null;
  // camelCase aliases for compatibility
  fullName?: string;
  roleId?: string | null;
  facilityId?: string | null;
  pathologistMaximumDailyCases?: number | null;
  isActive?: boolean;
  isDefaultQaReviewer?: boolean;
  createdAt?: string;
  updatedAt?: string;
  is_remote?: boolean;
  isRemote?: boolean;
  no_shipping?: boolean;
  noShipping?: boolean;
  remote_address_street?: string | null;
  remoteAddressStreet?: string | null;
  remote_address_city?: string | null;
  remoteAddressCity?: string | null;
  remote_address_state?: string | null;
  remoteAddressState?: string | null;
  remote_address_zip?: string | null;
  remoteAddressZip?: string | null;
  address?: RemoteShippingAddressFields | null;
  remoteAddress?: RemoteShippingAddressFields | null;
}

export interface CreateUserInput {
  username: string;
  email: string;
  full_name: string;
  credentials?: string;
  role_id?: string;
  facility_id?: string | null;
  pathologist_maximum_daily_cases?: number;
  is_active?: boolean;
  is_remote?: boolean;
  no_shipping?: boolean;
}

export interface UpdateUserInput extends Partial<CreateUserInput> {
  id: string;
  groupIds?: string[];
  is_default_qa_reviewer?: boolean;
}

export interface RemoteShippingAddressFields {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

export interface UserRemoteShippingAddressPayload {
  is_remote: boolean;
  no_shipping?: boolean;
  address?: RemoteShippingAddressFields;
}

export interface UserRemoteShippingAddress
  extends UserRemoteShippingAddressPayload {
  userId: string;
  legacyRemoteAddress?: string | null;
}

// Normalize user to have both snake_case and camelCase properties
// Backend returns camelCase, frontend components expect snake_case
function normalizeUser(user: any): User {
  return {
    ...user,
    // Map camelCase (from backend) to snake_case (for frontend components)
    full_name: user.full_name || user.fullName,
    credentials: user.credentials ?? null,
    role_id: user.role_id || user.roleId,
    facility_id: user.facility_id || user.facilityId,
    pathologist_maximum_daily_cases:
      user.pathologist_maximum_daily_cases ?? user.pathologistMaximumDailyCases,
    is_active: user.is_active ?? user.isActive ?? false,
    created_at: user.created_at || user.createdAt,
    updated_at: user.updated_at || user.updatedAt,
    // Also keep camelCase aliases
    fullName: user.fullName || user.full_name,
    roleId: user.roleId || user.role_id,
    facilityId: user.facilityId || user.facility_id,
    pathologistMaximumDailyCases:
      user.pathologistMaximumDailyCases ?? user.pathologist_maximum_daily_cases,
    isActive: user.isActive ?? user.is_active ?? false,
    is_default_qa_reviewer:
      user.is_default_qa_reviewer ?? user.isDefaultQaReviewer ?? false,
    isDefaultQaReviewer:
      user.isDefaultQaReviewer ?? user.is_default_qa_reviewer ?? false,
    createdAt: user.createdAt || user.created_at,
    updatedAt: user.updatedAt || user.updated_at,
    // Handle userGroups
    userGroups: user.userGroups || [],
    // Invitation tracking
    email: user.email,
    invitationStatus:
      user.invitationStatus || user.invitation_status || "not_invited",
    invitation_status:
      user.invitation_status || user.invitationStatus || "not_invited",
    invitedAt: user.invitedAt || user.invited_at || null,
    invited_at: user.invited_at || user.invitedAt || null,
    activatedAt: user.activatedAt || user.activated_at || null,
    activated_at: user.activated_at || user.activatedAt || null,
    warning: user.warning,
    passkeyCount: user.passkeyCount ?? user.passkey_count ?? 0,
    signatureImageUrl: user.signatureImageUrl ?? null,
    signatureImagePreviewUrl: user.signatureImagePreviewUrl ?? null,
    is_remote: user.is_remote ?? user.isRemote ?? false,
    isRemote: user.isRemote ?? user.is_remote ?? false,
    no_shipping: user.no_shipping ?? user.noShipping ?? false,
    noShipping: user.noShipping ?? user.no_shipping ?? false,
    remote_address_street:
      user.remote_address_street ||
      user.remoteAddressStreet ||
      user.address?.street ||
      null,
    remoteAddressStreet:
      user.remoteAddressStreet ||
      user.remote_address_street ||
      user.address?.street ||
      null,
    remote_address_city:
      user.remote_address_city ||
      user.remoteAddressCity ||
      user.address?.city ||
      null,
    remoteAddressCity:
      user.remoteAddressCity ||
      user.remote_address_city ||
      user.address?.city ||
      null,
    remote_address_state:
      user.remote_address_state ||
      user.remoteAddressState ||
      user.address?.state ||
      null,
    remoteAddressState:
      user.remoteAddressState ||
      user.remote_address_state ||
      user.address?.state ||
      null,
    remote_address_zip:
      user.remote_address_zip ||
      user.remoteAddressZip ||
      user.address?.zip ||
      null,
    remoteAddressZip:
      user.remoteAddressZip ||
      user.remote_address_zip ||
      user.address?.zip ||
      null,
    address: user.address || user.remoteAddress || null,
    remoteAddress: user.remoteAddress || user.address || null,
  };
}

export const usersService = {
  getAll: async () => {
    const users = await api.get<User[]>("/admin/users");
    return users.map(normalizeUser);
  },

  getActive: async () => {
    const users = await api.get<User[]>("/admin/users", {
      params: { is_active: true },
    });
    return users.map(normalizeUser);
  },

  getPathologists: async () => {
    const users = await api.get<User[]>("/admin/users/pathologists");
    return users.map(normalizeUser);
  },

  getById: async (id: string) => {
    const user = await api.get<User>(`/admin/users/${id}`);
    return normalizeUser(user);
  },

  create: async (input: CreateUserInput) => {
    const user = await api.post<User>("/admin/users", input);
    return normalizeUser(user);
  },

  update: async (input: UpdateUserInput) => {
    const { id, ...updates } = input;
    const user = await api.put<User>(`/admin/users/${id}`, updates);
    return normalizeUser(user);
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/users/${id}`);
  },

  toggleActive: async (id: string, isActive: boolean) => {
    const user = await api.put<User>(`/admin/users/${id}`, {
      is_active: isActive,
    });
    return normalizeUser(user);
  },

  setDefaultQaReviewer: async (userId: string) => {
    const user = await api.patch<User>(
      `/admin/users/${userId}/set-default-qa-reviewer`
    );
    return normalizeUser(user);
  },

  clearDefaultQaReviewer: async (userId: string) => {
    const user = await api.patch<User>(
      `/admin/users/${userId}/clear-default-qa-reviewer`
    );
    return normalizeUser(user);
  },

  updateRemoteShippingAddress: async (
    userId: string,
    payload: UserRemoteShippingAddressPayload
  ): Promise<UserRemoteShippingAddress> => {
    return api.put<UserRemoteShippingAddress>(
      `/admin/users/${userId}/remote-shipping-address`,
      payload
    );
  },
};
